import {
  Banner,
  BlockStack,
  Button,
  Image,
  InlineError,
  InlineStack,
  LegacyStack,
  Link,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris'
import { SearchIcon } from '@shopify/polaris-icons'
import { useCallback, useMemo, useState } from 'react'

import { Result } from '../common/api'
import { gidToId, ResultToast, useDialogActivator } from '../common/helpers'
import { SellingPlanInterval, SellingPlanPricingPolicyAdjustmentType } from '../common/plan-models'
import { PriceAdjustmentValueField } from '../common/PriceAdjustmentFields'
import { ProductModal, ProductResult } from '../common/ProductModal'
import { IntervalCountField, IntervalField } from '../common/ScheduleFields'
import { plansUrl } from '../common/urls'
import { createSellingPlanGroup } from '../selling-plan-page/api'
import { SellingPlanGroup as SellingPlanGroupFullObj } from '../selling-plan-page/models-common'
import {
  initialSellingPlanGroupFormData,
  SellingPlanGroupFormData,
  sellingPlanGroupFormDataWithDefaultValues,
} from '../selling-plan-page/models-form'
import {
  sellingPlanGroupFormDataToInput,
  sellingPlanGroupFormDataToPWData,
} from '../selling-plan-page/models-input'
import { attachProducts } from '../selling-plan-products-page/ProductList'
import { FreeSetupBanner } from './GettingStarted'

export const SellingPlanCreate = ({
  saveResult,
  hasSellingPlans,
  setSaveResult,
  onComplete,
}: {
  saveResult: Result<any> | null
  hasSellingPlans: boolean
  setSaveResult: (result: Result<any> | null) => void
  onComplete: (result: Result<SellingPlanGroupFullObj> | null) => void
}) => {
  const { mdUp } = useBreakpoints()
  const productModalActivator = useDialogActivator()
  const [product, setProduct] = useState<ProductResult | null>(null)

  const initialData = useMemo(() => {
    const spg = initialSellingPlanGroupFormData()
    spg.sellingPlans[0].deliveryPolicy.recurring.interval = SellingPlanInterval.MONTH
    spg.sellingPlans[0].fixedPricingPolicy.adjustmentType =
      SellingPlanPricingPolicyAdjustmentType.PERCENTAGE
    spg.sellingPlans[0].fixedPricingPolicy.adjustmentValue = '10'
    return spg
  }, [])

  const [sellingPlanGroupFormData, setSellingPlanGroupFormData] =
    useState<SellingPlanGroupFormData>(initialData)

  const [submitting, setSubmitting] = useState(false)

  const handleIntervalCountChange = useCallback(
    (val: number) => {
      const spgNewData = { ...sellingPlanGroupFormData } as SellingPlanGroupFormData
      spgNewData.sellingPlans[0].deliveryPolicy.recurring.intervalCount = val
      setSellingPlanGroupFormData(spgNewData)
    },
    [sellingPlanGroupFormData, setSellingPlanGroupFormData]
  )
  const handleIntervalChange = useCallback(
    (val: SellingPlanInterval) => {
      const spgNewData = { ...sellingPlanGroupFormData } as SellingPlanGroupFormData

      if (spgNewData.sellingPlans) {
        spgNewData.sellingPlans[0].deliveryPolicy.recurring.interval = val
      }

      setSellingPlanGroupFormData(spgNewData)
    },
    [sellingPlanGroupFormData, setSellingPlanGroupFormData]
  )

  const handleAdjustmentValueChange = useCallback(
    (val: string) => {
      const spgNewData = { ...sellingPlanGroupFormData } as SellingPlanGroupFormData

      if (spgNewData.sellingPlans) {
        spgNewData.sellingPlans[0].fixedPricingPolicy.adjustmentValue = val
      }

      setSellingPlanGroupFormData(spgNewData)
    },
    [sellingPlanGroupFormData, setSellingPlanGroupFormData]
  )

  const handleProductSelect = useCallback(
    (selected: ProductResult) => {
      setProduct(selected)
      productModalActivator.close()
    },
    [setProduct, productModalActivator]
  )

  const handleSubmit = useCallback(async () => {
    if (!product) {
      setSaveResult({
        status: 'error',
        message: 'Product is required',
        temporary: false,
        field: 'product',
      })
      return
    }

    const fixedValues = sellingPlanGroupFormDataWithDefaultValues(sellingPlanGroupFormData)

    const input = sellingPlanGroupFormDataToInput(fixedValues)
    const pwData = sellingPlanGroupFormDataToPWData(fixedValues)

    setSubmitting(true)

    const res = await createSellingPlanGroup(input, pwData)

    if (res.status !== 'success' || !res.data?.gid) {
      setSaveResult(res)
      setSubmitting(false)
      return
    }

    const attachRes = await attachProducts(gidToId(res.data.gid), [product.id])

    setSubmitting(false)
    onComplete(res)

    if (attachRes.status !== 'success') {
      setSaveResult(attachRes)
      return
    }

    setSaveResult(res)
  }, [sellingPlanGroupFormData, product, onComplete, setSaveResult])

  return (
    <>
      <ResultToast result={saveResult} setResult={setSaveResult} />
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2" fontWeight="bold">
          Quick Start - Create a subscription plan
        </Text>
        <BlockStack gap="600">
          <BlockStack gap="400">
            {hasSellingPlans && (
              <Banner tone="info">
                <p>
                  You already have selling plans! <Link url={plansUrl}>Click here</Link> to attach
                  more products or customize their settings.
                </p>
              </Banner>
            )}
            {!hasSellingPlans && <FreeSetupBanner />}
            <InlineStack gap="800" wrap={false} blockAlign="center">
              <LegacyStack.Item fill>
                <BlockStack gap="400">
                  <InlineStack wrap={false} gap="400" blockAlign="start">
                    <LegacyStack.Item fill>
                      <TextField
                        label={
                          <Text as="span" variant="bodyMd" tone="base">
                            Product
                          </Text>
                        }
                        readOnly={true}
                        value={product?.title ?? ''}
                        connectedRight={
                          <Button
                            icon={SearchIcon}
                            onClick={() => {
                              setSaveResult(null)
                              productModalActivator.show()
                            }}
                            size="large"
                          />
                        }
                        autoComplete="off"
                        error={
                          saveResult?.status === 'error' && saveResult.field === 'product'
                            ? saveResult.message
                            : false
                        }
                      />
                    </LegacyStack.Item>
                    <PriceAdjustmentValueField
                      label="Discount"
                      name="adjustmentValue"
                      onChange={handleAdjustmentValueChange}
                      prePaidDeliveries={1}
                      adjustmentType={SellingPlanPricingPolicyAdjustmentType.PERCENTAGE}
                      currencySymbol={'$'}
                      value={
                        sellingPlanGroupFormData.sellingPlans[0].fixedPricingPolicy.adjustmentValue
                      }
                      min={0}
                      max={100}
                    />
                  </InlineStack>
                  <LegacyStack distribution="fillEvenly">
                    <IntervalCountField
                      name="intervalCount"
                      id="intervalCount"
                      label="Deliver"
                      onChange={handleIntervalCountChange}
                      value={
                        sellingPlanGroupFormData.sellingPlans[0].deliveryPolicy.recurring
                          .intervalCount
                      }
                    />
                    <IntervalField
                      name="interval"
                      id="interval"
                      label={'  '}
                      onChange={handleIntervalChange}
                      value={
                        sellingPlanGroupFormData.sellingPlans[0].deliveryPolicy.recurring.interval
                      }
                    />
                  </LegacyStack>
                  {saveResult?.status === 'error' &&
                    saveResult.message &&
                    saveResult.field !== 'product' && (
                      <InlineError message={saveResult.message} fieldID="" />
                    )}
                </BlockStack>
              </LegacyStack.Item>
              {mdUp && (
                <LegacyStack.Item>
                  <Image
                    style={{ maxWidth: '120px', margin: '20px 70px 0' }}
                    source="/images/check-boxes.svg"
                    alt="Start Selling"
                  />
                </LegacyStack.Item>
              )}
            </InlineStack>
          </BlockStack>
          <InlineStack gap="400" blockAlign="center">
            <Button onClick={handleSubmit} loading={submitting} disabled={false} variant="primary">
              Create plan
            </Button>
            <Button onClick={() => onComplete(null)} variant="plain">
              Skip
            </Button>
          </InlineStack>
        </BlockStack>
      </BlockStack>
      <ProductModal
        open={productModalActivator.open}
        onClose={productModalActivator.close}
        onSelect={handleProductSelect}
      />
    </>
  )
}
